import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import { LegacyBenchmarkType } from "graphql-types/graphql";
import {
    useAssetFilterTranslations,
    useBenchmarkTranslations,
    useRegionTranslation,
} from "hooks";
import { BenchmarkBoxSkeleton } from "legacyNext/components";
import { useAssetBenchmarksQuery } from "legacyNext/hooks";

import { Benchmark } from "./Benchmark";
import { S } from "./benchmarks.styles";

type Props = {
    assetId: string;
};

export const Benchmarks = (props: Props) => {
    const { assetId } = props;
    const { t } = useTranslation();
    const { translations } = useBenchmarkTranslations();
    const { getRegionName } = useRegionTranslation();
    const { translations: filterTranslations } = useAssetFilterTranslations();

    const { data, loading } = useAssetBenchmarksQuery(assetId);

    if (loading || !data) {
        return (
            <S.BenchmarkList>
                <BenchmarkBoxSkeleton />
            </S.BenchmarkList>
        );
    }

    const list = data.map((benchmark) => {
        const {
            numberOfBuildings,
            co2BenchmarkMethod1,
            benchmarkType,
            benchmarkId,
        } = benchmark;

        const segmentLabel = match(benchmark)
            .with(
                { benchmarkType: LegacyBenchmarkType.BBR_PROPERTY_TYPE },
                ({ benchmarkType, segment }) =>
                    translations[`${benchmarkType}-${segment}`]
            )
            .with(
                { benchmarkType: LegacyBenchmarkType.NATIONAL },
                ({ segment }) => getRegionName(segment)
            )
            .with(
                { benchmarkType: LegacyBenchmarkType.BUILDING_SIZE },
                ({ segment }) => `${segment} ${t("filter.m2", "m²")}`
            )
            .with(
                { benchmarkType: LegacyBenchmarkType.REGION },
                ({ segment }) => segment.split("_")[1]
            )
            .otherwise(
                ({ segment }) =>
                    filterTranslations[segment as LegacyBenchmarkType] ??
                    segment
            );

        return (
            <Benchmark
                key={benchmarkId}
                title={filterTranslations[benchmarkType as LegacyBenchmarkType]}
                segment={segmentLabel}
                value={co2BenchmarkMethod1}
                numberOfBuildings={numberOfBuildings}
            />
        );
    });

    return <S.BenchmarkList>{list}</S.BenchmarkList>;
};
